/**
 * 带自动重试的 React.lazy 包装器
 *
 * 解决部署更新后旧 chunk 文件被替换，用户浏览器缓存的 index.html
 * 仍引用旧 hash 文件导致 "Importing a module script failed" 的问题。
 *
 * 策略：模块加载失败时自动刷新页面（仅一次，防止死循环）
 */
import { lazy, type ComponentType } from 'react';

const REFRESH_KEY = 'jointo_chunk_retry';

/**
 * 判断是否为 chunk 加载失败的错误
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('dynamically imported module')
  );
}

/**
 * 处理 chunk 加载失败：检查重试标记，未重试过则刷新页面
 */
function handleChunkError(error: unknown): never {
  if (!isChunkLoadError(error)) {
    throw error;
  }

  const hasRetried = sessionStorage.getItem(REFRESH_KEY);
  if (hasRetried) {
    sessionStorage.removeItem(REFRESH_KEY);
    throw error;
  }

  sessionStorage.setItem(REFRESH_KEY, '1');
  window.location.reload();

  // reload 后不会执行，但 TS 需要 never
  throw error;
}

/**
 * 带重试的 React.lazy
 *
 * 支持标准用法和 .then() 命名导出映射：
 *   lazyWithRetry(() => import('./Foo'))
 *   lazyWithRetry(() => import('./Foo').then(m => ({ default: m.Foo })))
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() =>
    factory().then(
      (mod) => {
        sessionStorage.removeItem(REFRESH_KEY);
        return mod;
      },
      (error) => handleChunkError(error)
    )
  );
}
